import styled from "@emotion/styled";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Modal, Stack } from "@mui/material";
import {
  Button,
  PhoneInputField,
  Radio,
  Select,
  TextField,
  Upload
} from "components/UI";
import { DateField } from "components/UI/InputFields/DateField";
import { MutationError } from "components/UI/MutationError";
import { IMAGE_URL } from "config";
import { formatISO, parseISO } from "date-fns";
import { omit } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useUpdatePartner } from "../api";
import { useMembersStore } from "../stores";
import { updatePartnerSchema } from "../validation";

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: auto;
  gap: 24px;
  align-items: flex-end;
`;

export function UpdatePartnerModal() {
  const updatePartnerModalOpen = useMembersStore(
    state => state.updatePartnerModalOpen
  );
  const closeUpdatePartnerModal = useMembersStore(
    state => state.closeUpdatePartnerModal
  );
  const partnerPreviewData = useMembersStore(state => state.partnerPreviewData);
  const partnerMutation = useUpdatePartner();
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm({
    defaultValues: {
      first_name: partnerPreviewData?.first_name || "",
      last_name: partnerPreviewData?.last_name || "",
      gender: partnerPreviewData?.gender || "",
      birth_date: partnerPreviewData?.birth_date
        ? parseISO(partnerPreviewData.birth_date)
        : null,
      phone: partnerPreviewData?.phone || "",
      email: partnerPreviewData?.email || "",
      address: partnerPreviewData?.address || "",
      partner_type: partnerPreviewData?.partner_type || "",
      organization_name: partnerPreviewData?.organization_name || "",
      image: null
    },
    resolver: yupResolver(updatePartnerSchema)
  });

  const partnerType = watch("partner_type");

  useEffect(() => {
    if (!partnerPreviewData) return;

    const partnerData = {
      first_name: partnerPreviewData?.first_name || "",
      last_name: partnerPreviewData?.last_name || "",
      gender: partnerPreviewData?.gender?.toLowerCase() || "",
      birth_date: partnerPreviewData?.birth_date
        ? parseISO(partnerPreviewData.birth_date)
        : null,
      phone: partnerPreviewData?.phone || "",
      email: partnerPreviewData?.email || "",
      address: partnerPreviewData?.address || "",
      partner_type: partnerPreviewData?.partner_type || "",
      organization_name: partnerPreviewData?.organization_name || "",
      image: null
    };

    reset(partnerData);
  }, [partnerPreviewData]);

  const onSubmit = values => {
    const formData = new FormData();

    if (values.image) {
      const data = { ...values, image: values.image };

      for (let key in data) {
        formData.append(key, data[key]);
      }
    } else {
      const data = omit(values, ["image", "birth_date"]);

      for (let key in data) {
        formData.append(key, data[key]);
      }
    }

    if (values.birth_date) {
      formData.set(
        "birth_date",
        formatISO(values.birth_date, {
          representation: "date"
        })
      );
    }

    // Ensure organization_name only sent when relevant
    if (values.partner_type !== "organization") {
      formData.delete("organization_name");
    }

    partnerMutation.mutate({
      partnerId: partnerPreviewData?.id,
      partnerData: formData
    });
  };

  return (
    <Modal open={updatePartnerModalOpen} onClose={closeUpdatePartnerModal}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          borderRadius: 2,
          width: { xs: "95vw", md: "880px" },
          maxHeight: "90vh",
          overflowX: "hidden",
          overflowY: "auto"
        }}
      >
        <Box
          sx={{
            p: 2,
            backgroundColor: "primary.main",
            color: "primary.contrastText"
          }}
        >
          {t("members.partners.UpdatePartner")}
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            py: 4,
            px: 8,
            backgroundColor: "white"
          }}
          spacing={2}
        >
          <FormGrid>
            <Select
              label={t("members.partners.PartnerType")}
              name="partner_type"
              options={[
                {
                  optionLabel: t("members.partners.Individual"),
                  value: "individual"
                },
                {
                  optionLabel: t("members.partners.Organization"),
                  value: "organization"
                }
              ]}
              register={register}
              error={!!errors.partner_type?.message}
              helperText={errors.partner_type?.message}
              placeholder={t("members.partners.SelectPartnerType")}
            />
            {partnerType === "organization" && (
              <Box sx={{ gridColumn: "span 2" }}>
                <TextField
                  label={t("members.partners.OrganizationName")}
                  name="organization_name"
                  error={!!errors.organization_name?.message}
                  helperText={errors.organization_name?.message}
                  register={register}
                  required
                />
              </Box>
            )}
            <TextField
              label={`${
                partnerType === "individual"
                  ? t("form.common.firstName")
                  : t("members.partners.ContactPersonFirstName")
              }`}
              name="first_name"
              error={!!errors.first_name?.message}
              helperText={errors.first_name?.message}
              register={register}
            />
            <TextField
              label={`${
                partnerType === "individual"
                  ? t("form.common.fatherName")
                  : t("members.partners.ContactPersonLastName")
              }`}
              name="last_name"
              error={!!errors.last_name?.message}
              helperText={errors.last_name?.message}
              register={register}
            />
            {partnerType === "individual" && (
              <Radio
                label={t("form.common.gender")}
                name="gender"
                buttons={[
                  {
                    radioLabel: t("form.common.male"),
                    value: "male",
                    id: "male"
                  },
                  {
                    radioLabel: t("form.common.female"),
                    value: "female",
                    id: "female"
                  }
                ]}
                row={true}
                register={register}
                error={!!errors.gender?.message}
                helperText={errors.gender?.message}
              />
            )}
            {partnerType === "individual" && (
              <Box sx={{ gridColumn: "span 2" }}>
                <Controller
                  name="birth_date"
                  control={control}
                  render={({ field }) => (
                    <DateField
                      label={t("form.common.dateOfBirth")}
                      name="birth_date"
                      error={!!errors.birth_date?.message}
                      helperText={errors.birth_date?.message}
                      {...field}
                      disableFutureDates
                    />
                  )}
                />
              </Box>
            )}
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <PhoneInputField
                  label={`${
                    partnerType === "individual"
                      ? t("form.common.phone")
                      : t("members.partners.OrganizationPhone")
                  }`}
                  variant="filled"
                  error={!!errors.phone?.message}
                  helperText={errors.phone?.message}
                  {...field}
                />
              )}
            />
            <TextField
              label={`${
                partnerType === "individual"
                  ? t("form.common.email")
                  : t("members.partners.OrganizationEmail")
              }`}
              name="email"
              error={!!errors.email?.message}
              helperText={errors.email?.message}
              register={register}
            />
            <TextField
              label={`${
                partnerType === "individual"
                  ? t("form.common.address")
                  : t("members.partners.OrganizationAddress")
              }`}
              name="address"
              error={!!errors.address?.message}
              helperText={errors.address?.message}
              register={register}
            />
            <Controller
              name="image"
              control={control}
              render={({ field }) => (
                <Box sx={{ gridColumn: "span 3" }}>
                  <Upload
                    title={`${
                      partnerType === "individual"
                        ? t("addMembers.step1.UploadPhoto")
                        : t("members.partners.OrganizationLogo")
                    }`}
                    buttonTitle={t("ui.dropzone.browse")}
                    error={!!errors.image?.message}
                    helperText={errors.image?.message}
                    onChange={field.onChange}
                    preview={`${IMAGE_URL}/${partnerPreviewData?.image_url}`}
                  />
                </Box>
              )}
            />
          </FormGrid>
          <MutationError error={partnerMutation.error} sx={{ mt: 2 }} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={2}
            sx={{ pt: 6 }}
          >
            <Button
              size="large"
              variant="contained"
              disabled={partnerMutation.isLoading}
              color="secondary"
              flavor="secondary"
              onClick={closeUpdatePartnerModal}
            >
              {t("form.common.cancel")}
            </Button>
            <Button
              type="submit"
              size="large"
              variant="contained"
              disabled={partnerMutation.isLoading}
            >
              {t("form.common.update")}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}
